/**
 * Generic CRUD client factory (issue #1406, part of epic #1402)
 *
 * ~20 entity types in `lib/apiClient.ts` each hand-wrote the same
 * create/update/delete triplet: `POST /<resource>`, `PUT /<resource>/:id`,
 * `DELETE /<resource>/:id[?hard=true]`, all wrapping `apiRequest`. This
 * factory collapses that boilerplate to one call per entity; the per-entity
 * modules under `lib/apiClient/` only declare the resource path and types.
 *
 * Entities whose write shape genuinely differs from this flat
 * `POST /x`, `PUT /x/:id`, `DELETE /x/:id` pattern — composite keys
 * (`Dependency`), nested parent-scoped paths (`TechnologyVersion`), or a
 * double-wrapped response envelope (`TechnologyProductFamily`) — are kept
 * hand-written in their own module rather than forced through this factory.
 */

import { apiRequest } from './http';

/**
 * Bulk operation result — mirrors the backend BulkOperationResult type
 */
export interface BulkOperationResult {
  succeeded: string[];
  failed: Array<{ id: string; error: string }>;
}

export interface EntityCrudClient<TEntity, TCreate, TUpdate> {
  create(input: TCreate): Promise<TEntity>;
  update(id: string, input: TUpdate): Promise<TEntity>;
  /**
   * `hardDelete` defaults to `false` (soft delete). Passing `true` appends
   * `?hard=true` to the DELETE request.
   */
  remove(id: string, hardDelete?: boolean): Promise<void>;
}

/**
 * Build a `{ create, update, remove }` client for a flat REST resource at
 * `resourcePath` (e.g. `/services`), preserving the exact request shape
 * every hand-written triplet in the former `apiClient.ts` used.
 */
export function createEntityCrudClient<TEntity, TCreate, TUpdate>(
  resourcePath: string
): EntityCrudClient<TEntity, TCreate, TUpdate> {
  return {
    create(input: TCreate): Promise<TEntity> {
      return apiRequest<TEntity>(resourcePath, {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
    update(id: string, input: TUpdate): Promise<TEntity> {
      return apiRequest<TEntity>(`${resourcePath}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      });
    },
    remove(id: string, hardDelete: boolean = false): Promise<void> {
      const queryParam = hardDelete ? '?hard=true' : '';
      return apiRequest<void>(`${resourcePath}/${id}${queryParam}`, {
        method: 'DELETE',
      });
    },
  };
}

export interface BulkOperationsClient<TUpdate> {
  bulkDelete(ids: string[], hardDelete?: boolean): Promise<BulkOperationResult>;
  bulkUpdate(ids: string[], updates: TUpdate): Promise<BulkOperationResult>;
}

/**
 * Build a `{ bulkDelete, bulkUpdate }` client for a flat REST resource's
 * `/bulk-delete` and `/bulk-update` endpoints.
 */
export function createBulkOperationsClient<TUpdate>(
  resourcePath: string
): BulkOperationsClient<TUpdate> {
  return {
    bulkDelete(ids: string[], hardDelete = false): Promise<BulkOperationResult> {
      return apiRequest<BulkOperationResult>(`${resourcePath}/bulk-delete`, {
        method: 'POST',
        body: JSON.stringify({ ids, hard: hardDelete }),
      });
    },
    bulkUpdate(ids: string[], updates: TUpdate): Promise<BulkOperationResult> {
      return apiRequest<BulkOperationResult>(`${resourcePath}/bulk-update`, {
        method: 'PUT',
        body: JSON.stringify({ ids, updates }),
      });
    },
  };
}
